/**
 * Engagement template schemas — internal post-win setup packages.
 * A template spawns a full set of recurring compliance items for a client in
 * one click. NOT client-facing, NOT proposals, NOT engagement letters.
 *
 * OPS 3.7 step 3: the direction REVERSED — template serviceTypes ARE the
 * catalog. Compliance service-type pickers derive their options from the
 * distinct serviceTypes on a jurisdiction's active templates (see
 * GET /v1/templates/service-types); the hardcoded
 * COMPLIANCE_SERVICES_BY_JURISDICTION list is deprecated. New service types
 * are born here, in Admin → Compliance Templates CRUD — no code changes.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, booleanQuerySchema } from './common';
import { recurrenceFrequencySchema } from './compliance-item';
import { JURISDICTION_CODES } from '../reference/jurisdictions';

const jurisdictionSchema = z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]);

export const templateItemInputSchema = z.object({
  serviceType: z.string().min(1).max(100),
  title: z.string().min(1).max(200),
  recurrenceFrequency: recurrenceFrequencySchema,
  ordering: z.number().int().min(0).default(0),
  notes: z.string().max(2000).nullable().optional(),
  /** Pre-checked in the apply modal; optional coverage items stay opt-in. */
  defaultIncluded: z.boolean().default(false),
  /** Non-tax service due date = period end + this offset; reviewed tax filing dates do not use it. */
  dueDateOffsetDays: z.number().int().min(1).max(365).nullable().optional(),
});
export type TemplateItemInput = z.infer<typeof templateItemInputSchema>;

export const templateItemOutputSchema = z.object({
  id: idSchema,
  serviceType: z.string(),
  title: z.string(),
  recurrenceFrequency: recurrenceFrequencySchema,
  ordering: z.number().int(),
  notes: z.string().nullable(),
  defaultIncluded: z.boolean(),
  dueDateOffsetDays: z.number().int().nullable(),
  /** Server-computed default first deadline (same rule apply uses). */
  firstDeadlineDefault: iso8601Schema,
});
export type TemplateItemOutput = z.infer<typeof templateItemOutputSchema>;

export const templateOutputSchema = z.object({
  id: idSchema,
  name: z.string(),
  jurisdiction: jurisdictionSchema,
  description: z.string().nullable(),
  active: z.boolean(),
  items: z.array(templateItemOutputSchema),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type TemplateOutput = z.infer<typeof templateOutputSchema>;

export const createTemplateInputSchema = z.object({
  name: z.string().min(1).max(200),
  jurisdiction: jurisdictionSchema,
  description: z.string().max(2000).nullable().optional(),
  active: z.boolean().default(true),
  items: z.array(templateItemInputSchema).min(1).max(50),
});
export type CreateTemplateInput = z.infer<typeof createTemplateInputSchema>;

/** Items, when present, fully replace the template's item set (ordered as given). */
export const updateTemplateInputSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  jurisdiction: jurisdictionSchema.optional(),
  description: z.string().max(2000).nullable().optional(),
  active: z.boolean().optional(),
  items: z.array(templateItemInputSchema).min(1).max(50).optional(),
});
export type UpdateTemplateInput = z.infer<typeof updateTemplateInputSchema>;

export const listTemplatesInputSchema = z.object({
  active: booleanQuerySchema.optional(),
  /** Jurisdiction-driven catalog: narrow the list to one jurisdiction. */
  jurisdiction: jurisdictionSchema.optional(),
});
export type ListTemplatesInput = z.infer<typeof listTemplatesInputSchema>;

/** OPS 3.7 step 3: the compliance service-type catalog, derived from the
 *  distinct serviceTypes on a jurisdiction's ACTIVE templates (alphabetical).
 *  Powers every service-type picker — no hardcoded list anymore. */
export const serviceTypesInputSchema = z.object({
  jurisdiction: jurisdictionSchema,
});
export type ServiceTypesInput = z.infer<typeof serviceTypesInputSchema>;
export const serviceTypesOutputSchema = z.object({
  jurisdiction: jurisdictionSchema,
  items: z.array(z.string()),
});
export type ServiceTypesOutput = z.infer<typeof serviceTypesOutputSchema>;

export const applyTemplateItemOverrideSchema = z.object({
  templateItemId: idSchema,
  include: z.boolean(),
  /** Legacy non-tax period end / tax filing due input. */
  firstDeadline: iso8601Schema.optional(),
  firstEndDate: iso8601Schema.nullable().optional(),
  firstDueDate: iso8601Schema.nullable().optional(),
  evidenceDocumentId: idSchema.optional(),
  registryEvidenceCompanyNumber: z.string().regex(/^[A-Z0-9]{8}$/).optional(),
});
export type ApplyTemplateItemOverride = z.infer<typeof applyTemplateItemOverrideSchema>;

export const applyTemplateInputSchema = z.object({
  clientId: idSchema,
  engagementId: idSchema.nullable().optional(),
  retainerFork: z.boolean().optional(),
  /**
   * Optional per-item checklist: when present, only items with an override
   * entry of include=true are spawned (firstDeadline overrides the computed
   * default). Omitting itemOverrides keeps the original all-items behavior.
   */
  itemOverrides: z.array(applyTemplateItemOverrideSchema).max(50).optional(),
});
export type ApplyTemplateInput = z.infer<typeof applyTemplateInputSchema>;

export const applyTemplateItemResultSchema = z.object({
  itemId: idSchema.nullable(),
  serviceType: z.string(),
  title: z.string(),
  status: z.enum(['created', 'skipped']),
});
export type ApplyTemplateItemResult = z.infer<typeof applyTemplateItemResultSchema>;

export const applyTemplateOutputSchema = z.object({
  complianceClientId: idSchema.nullable(),
  created: z.number().int().min(0),
  skipped: z.number().int().min(0),
  items: z.array(applyTemplateItemResultSchema),
  clientStatus: z.enum(['LEAD','ACTIVE','INACTIVE','CHURNED']).optional(),
  retainerDecision: z.enum(['APPLIED','DEFERRED']).nullable().optional(),
});
export type ApplyTemplateOutput = z.infer<typeof applyTemplateOutputSchema>;
